import { formatPriceInLocale } from "@lemmonhq/orders";

export type CellFormat =
  | "text"
  | "money"
  | "integer"
  | "quantity"
  | "decimal"
  | "percentage"
  | "date"
  | "timestamp";

/** Shared value formatter for interactive cells/metrics, matching the PDF helpers. */
export function formatValue(
  value: unknown,
  format: CellFormat,
  currency: string,
  locale = "en",
): string {
  if (value == null || value === "") return format === "text" ? "" : "—";
  switch (format) {
    case "money":
      return formatPriceInLocale(Number(value) || 0, true, 2, currency);
    case "integer":
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
        Number(value) || 0,
      );
    // Unit counts are shown whole. A split payment or partial refund
    // recognizes a fraction of an item in the ledger, but "1'444.6 overnight
    // stays" is not a quantity a merchant can read; the value (and any total)
    // is rounded for display only.
    case "quantity":
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
        Number(value) || 0,
      );
    case "decimal":
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
    case "percentage":
      return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(Number(value) || 0)}%`;
    case "date":
    case "timestamp": {
      const d = new Date(String(value));
      if (Number.isNaN(d.getTime())) return String(value);
      return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        ...(format === "timestamp"
          ? { hour: "2-digit", minute: "2-digit" }
          : {}),
      }).format(d);
    }
    default:
      return String(value);
  }
}

/**
 * Compact value formatter for chart data labels and axis ticks — "19.7k", not
 * "19,700.00". Money keeps its symbol; small values stay exact so a CHF 8.50 bar
 * does not read as "9". Tooltips always show the full value, never this.
 */
export function formatCompact(
  value: unknown,
  format: CellFormat,
  currency: string,
  locale = "en",
): string {
  if (value == null || value === "") return "—";
  const n = Number(value) || 0;
  if (format === "percentage") return formatValue(n, format, currency, locale);
  if (format === "text" || format === "date" || format === "timestamp") {
    return formatValue(value, format, currency, locale);
  }

  const abs = Math.abs(n);
  // Below 1000 there is nothing to abbreviate — show the real number.
  if (abs < 1000) {
    return format === "integer"
      ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n)
      : formatValue(n, format, currency, locale);
  }

  const compact = new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  }).format(n);

  return format === "money" ? `${currency} ${compact}` : compact;
}

/**
 * Fixed categorical palette — assigned in order, never cycled by rank.
 *
 * Literal hex rather than DaisyUI tokens on purpose: the theme's semantic slots
 * are near-black (`secondary` #101923, `accent` #000/#fff) and two of them are
 * the same blue, so as a *categorical* palette they fail on chroma, lightness
 * band and contrast. These five steps pass all six checks of the dataviz
 * validator against both the light (#ffffff) and dark (#1d232a) chart surfaces,
 * so one palette serves all six Lemmon themes.
 *
 *   node scripts/validate_palette.js "#2f7ded,#e2622c,#0f9488,#9d4edd,#b8860b"
 *
 * Status colours (error/success) stay reserved for state and are never used as
 * a series colour.
 */
export const CHART_PALETTE = [
  "#2f7ded",
  "#e2622c",
  "#0f9488",
  "#9d4edd",
  "#b8860b",
];
